import { useCallback, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useUser } from "../../../../contexts/userContext/userContext";
import useCurrency from "./useCurrency";
import useCouponCode from "./useCouponCode";
import useCreditCard from "./useCreditCard";
import usePurchases from "./usePurchases";
import type { PurchaseParamsT } from "../models/models";
//@ts-ignore
import config from "../../../../config";
import { useContent } from "../../../../contexts/contentContext/contentContext";
import checkIfPrivatePage from "../helpers/checkIfPrivatePage";

/**
 * Custom hook for initializing the purchase page.
 *
 * It sets the currency, coupon code and set payment method and credit card
 * according to the billing cycle.
 */
const useInitializePurchasePage = () => {
  const { periodType, couponCode } = useParams<PurchaseParamsT>();
  const { redirectToCorrectPurchasePage } = usePurchases();
  const {
    setIsPurchaseFailed,
    setPaymentMethodAndCreditCardAccordingToBillingCycle,
    isCreditCardsLoading,
  } = useCreditCard();
  const { setCurrency } = useCurrency();
  const { isTokenExist, isLoggedIn } = useUser();
  const { getPath } = useContent();
  const { setCouponCode, validateCoupon } = useCouponCode();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Redirect to home page if user is not logged in
    // and user is trying to access the private page
    if (!isTokenExist && checkIfPrivatePage()) {
      // @ts-ignore
      window.location.href = config.baseurl + getPath() + "home";
    }
  }, [getPath(), isTokenExist]);

  // Set coupon code from URL
  useEffect(() => {
    if (couponCode || searchParams.get("coupon")) {
      setCouponCode(couponCode || (searchParams.get("coupon") as string));

      setTimeout(() => {
        validateCoupon(periodType!);
      }, 0);
    }
  }, [couponCode, searchParams.get("coupon"), periodType]);

  // Redirect to correct purchase page
  //
  useEffect(() => {
    redirectToCorrectPurchasePage();
    validateCoupon(periodType!);
  }, [periodType]);

  // Set currency according to user's country or billing cycle
  useEffect(() => {
    setCurrency();
  }, [isLoggedIn()]);

  // Sets payment method and credit card according to billing cycle
  useEffect(() => {
    if (isLoggedIn() && !isCreditCardsLoading) {
      setPaymentMethodAndCreditCardAccordingToBillingCycle();
    }
  }, [isCreditCardsLoading, isLoggedIn()]);

  const receiveMessage = useCallback(
    (event: MessageEvent) => {
      if (typeof event.data === "string") {
        // Get data from iframe end redirect to result page
        if (
          !event.data.includes("recaptcha") &&
          !event.data.includes("webpackHotUpdate") &&
          !event.data.includes("rpcToken") &&
          !event.data.includes("purchase-failed") &&
          !event.data.includes('"type":"authResult"')
        ) {
          const isLocalhost = window.location.port === "3000";

          console.log("Redirect from useInitializePurchasePage");

          if (isLocalhost) {
            window.location.href = event.data
              .replace("https", "http")
              .replace("localhost", "localhost:3000");
          }
          window.location.href = event.data;
        } else if (event.data.includes("purchase-failed")) {
          setIsPurchaseFailed(true);
        }
      }
    },
    [setIsPurchaseFailed]
  );

  useEffect(() => {
    window.addEventListener("message", receiveMessage, false);

    return () => {
      window.removeEventListener("message", receiveMessage);
    };
  }, [receiveMessage, setIsPurchaseFailed]);
};

export default useInitializePurchasePage;
